import { useEffect, useRef, useState } from 'react';
import { RoomEvent } from 'livekit-client';
import { useCandidateT } from '../../i18n/candidate';

/**
 * Formal interviewer avatar for the AI interview (org feature `ai_interview_avatar`).
 *
 * Two pre-made looping clips per gender play on the candidate's device — "talking" while
 * the AI speaks, "listening" otherwise — so it costs nothing per interview and never
 * enters the LiveKit room or the recording. Lips are not synced to the words.
 *
 * Clips live in client/public/interviewer-avatars/<male|female>/ (spec: docs/modules/ai-interviews.md).
 * Anything missing or failing shows `fallback` (the aura), never a blank box.
 */
export const AVATAR_GENDER_ATTRIBUTE = 'tc.voice.gender';
// Published by LiveKit Agents' AgentSession on every state change (listening/thinking/speaking).
export const AGENT_STATE_ATTRIBUTE = 'lk.agent.state';

// Back to "listening" only after the agent has been quiet this long, so the short pauses
// between sentences do not flicker the clips.
const LISTEN_HOLD_MS = 450;

const clip = (gender, name) => `/interviewer-avatars/${gender}/${name}.mp4`;

/** Latest value of a remote participant attribute (the agent's); null until published. */
function useAgentAttribute(room, key) {
  const [value, setValue] = useState(null);

  useEffect(() => {
    if (!room) return undefined;
    const read = () => {
      for (const p of room.remoteParticipants.values()) {
        const v = p.attributes?.[key];
        if (v) {
          setValue(v);
          return;
        }
      }
    };
    read();
    room.on(RoomEvent.ParticipantAttributesChanged, read);
    room.on(RoomEvent.ParticipantConnected, read);
    return () => {
      room.off(RoomEvent.ParticipantAttributesChanged, read);
      room.off(RoomEvent.ParticipantConnected, read);
    };
  }, [room, key]);

  return room ? value : null;
}

/** The gender of the voice the agent is actually using, published by the agent; null until known. */
export function useAgentVoiceGender(room, enabled) {
  const g = useAgentAttribute(enabled ? room : null, AVATAR_GENDER_ATTRIBUTE);
  return g === 'male' || g === 'female' ? g : null;
}

/**
 * Whether the agent's voice is playing, from the agent's own state — not from transcripts,
 * whose final event can be dropped by de-duplication and leave "speaking" stuck on.
 * null until the agent publishes a state (caller falls back to its own signal).
 */
export function useAgentSpeaking(room) {
  const state = useAgentAttribute(room, AGENT_STATE_ATTRIBUTE);
  return state ? state === 'speaking' : null;
}

export function InterviewerAvatar({ gender, speaking, fallback, className = '' }) {
  const talkingRef = useRef(null);
  const listeningRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [talking, setTalking] = useState(speaking);
  const t = useCandidateT();

  useEffect(() => {
    setReady(false);
    setFailed(false);
  }, [gender]);

  // Talk at once; return to listening only after a short quiet spell.
  useEffect(() => {
    if (speaking) {
      setTalking(true);
      return undefined;
    }
    const t = setTimeout(() => setTalking(false), LISTEN_HOLD_MS);
    return () => clearTimeout(t);
  }, [speaking]);

  // Every clip's first frame is the same resting portrait, so restarting the incoming clip
  // makes the cross-fade blend two near-identical poses instead of jumping mid-motion.
  // Autoplay can be refused (e.g. iOS Low Power Mode); the clip then stays on its first
  // frame, which is still the interviewer, so a rejected play() is not an error.
  useEffect(() => {
    const incoming = talking ? talkingRef.current : listeningRef.current;
    if (incoming && ready) incoming.currentTime = 0;
    for (const v of [talkingRef.current, listeningRef.current]) v?.play?.().catch(() => {});
  }, [talking, ready]);

  if (!gender || failed) return fallback;

  const video = (ref, name, visible) => (
    <video
      ref={ref}
      src={clip(gender, name)}
      muted
      loop
      autoPlay
      playsInline
      preload="auto"
      disablePictureInPicture
      aria-hidden="true"
      onError={() => setFailed(true)}
      onCanPlay={name === 'listening' ? () => setReady(true) : undefined}
      className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ease-in-out ${visible ? 'opacity-100' : 'opacity-0'}`}
    />
  );

  return (
    <>
      {!ready && fallback}
      <div
        className={`relative overflow-hidden ${ready ? '' : 'hidden'} ${className}`}
        role="img"
        aria-label={talking ? t('Interviewer speaking') : t('Interviewer listening')}
      >
        {video(listeningRef, 'listening', !talking)}
        {video(talkingRef, 'talking', talking)}
      </div>
    </>
  );
}
